/* FFF Motion — cinematic motion system for For Fathers Fitness (Shopify / Dawn)
   One engine, driven by data-attributes. Transforms + opacity only. GSAP 3 + ScrollTrigger (cdnjs, deferred).
   Tiers: full | lite (touch / small / low-power) | reduced (prefers-reduced-motion).
   Primitives:
     [data-fff-scene]                 scroll-progress container (0..1 while it crosses the viewport)
     [data-fff-depth="0.35"]          scroll parallax plane inside a scene (fraction of scene travel)
     [data-fff-pointer="0.6"]         pointer parallax plane (px at full strength, scaled by config)
     [data-fff-reveal="mask-up|mask-right|fade|lines"]  one-shot reveal on entry ([data-fff-delay] ms)
     [data-fff-float]                 studio float + shadow drift (product presence)
     [data-fff-tilt="5"]              perspective card tilt on pointer (deg)
     [data-fff-magnetic]              magnetic button
     [data-fff-timeline='[...]'] + [data-fff-pin] + [data-fff-length="220"]  scrubbed scene (vh of scroll)
     [data-fff-ambient="gold|warm|cool|dark"]  section colour for the shared light layer
     [data-fff-particles="60"]        gold dust canvas inside the element
*/
(function () {
  'use strict';
  if (window.__fffMotion) return;
  var cfg = window.FFF_MOTION = Object.assign({
    intensity: 1,
    pointer: { strength: 18, ease: 0.08 },          // px per unit at full strength; lerp factor
    depth: { strength: 120 },                       // px of scroll travel per unit depth
    tilt: { max: 5, ease: 0.1 },
    magnetic: { radius: 110, strength: 0.32 },
    float: { y: 9, rot: 1.2, period: 6.5 },
    reveal: { duration: 1.1, stagger: 0.07, ease: 'expo.out', once: true },
    scrub: 0.6,
    particles: { full: 60, lite: 22, dpr: 1.5 },
    ambient: { ease: 0.05, colors: { gold: [231, 198, 107], warm: [255, 170, 90], cool: [120, 150, 190], dark: [40, 36, 30] } }
  }, window.FFF_MOTION || {});

  var mq = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var nav = navigator;
  var tier = mq('(prefers-reduced-motion: reduce)') ? 'reduced'
    : (mq('(hover: none)') || window.innerWidth < 990 || (nav.deviceMemory && nav.deviceMemory < 4) || (nav.connection && nav.connection.saveData)) ? 'lite' : 'full';
  var html = document.documentElement;
  html.setAttribute('data-fff-tier', tier);
  html.classList.add('fff-motion');
  var K = tier === 'lite' ? 0.5 : 1;               // translation multiplier on lite
  var raf = window.requestAnimationFrame.bind(window);
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };

  /* ---------- pointer state (normalised -1..1 from viewport centre) ---------- */
  var ptr = { x: 0, y: 0, tx: 0, ty: 0, live: false };
  if (tier === 'full') {
    window.addEventListener('pointermove', function (e) {
      ptr.tx = (e.clientX / window.innerWidth) * 2 - 1;
      ptr.ty = (e.clientY / window.innerHeight) * 2 - 1;
      ptr.live = true;
    }, { passive: true });
    document.addEventListener('pointerleave', function () { ptr.tx = 0; ptr.ty = 0; });
  }

  /* ---------- ambient light layer (fixed canvas behind content) ---------- */
  var amb = null;
  function initAmbient() {
    if (tier === 'reduced') return;
    var c = document.createElement('canvas'); c.id = 'fff-ambient'; c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;z-index:0;pointer-events:none;opacity:.9;';
    document.body.insertBefore(c, document.body.firstChild);
    var ctx = c.getContext('2d'), w, h;
    /* soft gradients only: render at most ~1280px wide and let CSS scale it (GPU-memory safe on 2K/4K + HiDPI) */
    function size() { var k = Math.min(1, 1280 / Math.max(1, window.innerWidth)); w = c.width = Math.max(1, Math.floor(window.innerWidth * k)); h = c.height = Math.max(1, Math.floor(window.innerHeight * k)); }
    size(); window.addEventListener('resize', size, { passive: true });
    var col = cfg.ambient.colors.gold.slice(), target = col.slice(), lx = 0.65, ly = 0.3, hidden = false;
    amb = { set: function (name) { target = (cfg.ambient.colors[name] || cfg.ambient.colors.gold).slice(); } };
    document.addEventListener('visibilitychange', function () { hidden = document.hidden; });
    function draw() {
      if (!hidden) {
        for (var i = 0; i < 3; i++) col[i] = lerp(col[i], target[i], cfg.ambient.ease);
        lx = lerp(lx, 0.62 + ptr.x * 0.12, 0.04); ly = lerp(ly, 0.32 + ptr.y * 0.1, 0.04);
        ctx.clearRect(0, 0, w, h);
        var rgb = col.map(Math.round).join(',');
        var g = ctx.createRadialGradient(w * lx, h * ly, 0, w * lx, h * ly, Math.max(w, h) * 0.75);
        g.addColorStop(0, 'rgba(' + rgb + ',0.13)'); g.addColorStop(0.45, 'rgba(' + rgb + ',0.04)'); g.addColorStop(1, 'rgba(' + rgb + ',0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
        var g2 = ctx.createRadialGradient(w * (1 - lx) * 0.6, h * 0.95, 0, w * (1 - lx) * 0.6, h * 0.95, Math.max(w, h) * 0.5);
        g2.addColorStop(0, 'rgba(' + rgb + ',0.05)'); g2.addColorStop(1, 'rgba(' + rgb + ',0)');
        ctx.fillStyle = g2; ctx.fillRect(0, 0, w, h);
      }
      raf(draw);
    }
    draw();
  }

  /* ---------- particles (gold dust) ---------- */
  function initParticles(el) {
    if (tier === 'reduced') return;
    var n = parseInt(el.getAttribute('data-fff-particles'), 10) || cfg.particles[tier === 'lite' ? 'lite' : 'full'];
    if (tier === 'lite') n = Math.min(n, cfg.particles.lite);
    var c = document.createElement('canvas'); c.className = 'fff-particles'; c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:' + (el.getAttribute('data-fff-particles-z') || 3) + ';';
    el.appendChild(c);
    var ctx = c.getContext('2d'), w, h, dpr = Math.min(window.devicePixelRatio || 1, cfg.particles.dpr, 1), on = false, ps = [];
    function size() { var r = el.getBoundingClientRect(); w = c.width = Math.max(1, Math.floor(r.width * dpr)); h = c.height = Math.max(1, Math.floor(r.height * dpr)); }
    size(); window.addEventListener('resize', size, { passive: true });
    for (var i = 0; i < n; i++) ps.push({ x: Math.random(), y: Math.random(), z: 0.3 + Math.random() * 0.7, s: Math.random() * 6.28, v: 0.00008 + Math.random() * 0.00018 });
    new IntersectionObserver(function (es) { on = es[0].isIntersecting; if (on) raf(draw); }, { rootMargin: '10%' }).observe(el);
    var t = 0;
    function draw() {
      if (!on) return;
      t += 1; ctx.clearRect(0, 0, w, h);
      var px = ptr.x * 0.02, py = ptr.y * 0.02;
      for (var i = 0; i < ps.length; i++) {
        var p = ps[i]; p.y -= p.v * p.z * 4; p.x += Math.sin(t * 0.004 + p.s) * 0.00025;
        if (p.y < -0.05) { p.y = 1.05; p.x = Math.random(); }
        var x = (p.x + px * p.z) * w, y = (p.y + py * p.z) * h, r = (0.6 + p.z * 1.6) * dpr;
        var a = (0.18 + 0.5 * p.z) * (0.6 + 0.4 * Math.sin(t * 0.02 + p.s));
        ctx.beginPath(); ctx.arc(x, y, r, 0, 6.283); ctx.fillStyle = 'rgba(231,198,107,' + a.toFixed(3) + ')'; ctx.fill();
      }
      raf(draw);
    }
  }

  /* ---------- pointer planes, tilt, magnetic (one RAF loop) ---------- */
  var planes = [], tilts = [], mags = [];
  function loop() {
    ptr.x = lerp(ptr.x, ptr.tx, cfg.pointer.ease); ptr.y = lerp(ptr.y, ptr.ty, cfg.pointer.ease);
    var i, o;
    for (i = 0; i < planes.length; i++) {
      o = planes[i]; if (!o.on) continue;
      var s = o.k * cfg.pointer.strength * cfg.intensity;
      o.el.style.setProperty('--fff-px', (ptr.x * s).toFixed(2) + 'px');
      o.el.style.setProperty('--fff-py', (ptr.y * s * 0.7).toFixed(2) + 'px');
    }
    for (i = 0; i < tilts.length; i++) {
      o = tilts[i]; o.rx = lerp(o.rx, o.trx, cfg.tilt.ease); o.ry = lerp(o.ry, o.try_, cfg.tilt.ease);
      if (Math.abs(o.rx) + Math.abs(o.ry) > 0.01) o.el.style.setProperty('--fff-rx', o.rx.toFixed(2) + 'deg'), o.el.style.setProperty('--fff-ry', o.ry.toFixed(2) + 'deg');
    }
    for (i = 0; i < mags.length; i++) {
      o = mags[i]; o.x = lerp(o.x, o.tx, 0.18); o.y = lerp(o.y, o.ty, 0.18);
      o.el.style.setProperty('--fff-mx', o.x.toFixed(2) + 'px'); o.el.style.setProperty('--fff-my', o.y.toFixed(2) + 'px');
    }
    raf(loop);
  }

  function initPointerPlanes(root) {
    if (tier !== 'full') return;
    root.querySelectorAll('[data-fff-pointer]').forEach(function (el) {
      if (el.__fffP) return; el.__fffP = true;
      var o = { el: el, k: parseFloat(el.getAttribute('data-fff-pointer')) || 0.3, on: false };
      el.classList.add('fff-plane');
      new IntersectionObserver(function (es) { o.on = es[0].isIntersecting; }, { rootMargin: '20%' }).observe(el);
      planes.push(o);
    });
  }
  function initTilt(root) {
    if (tier !== 'full') return;
    root.querySelectorAll('[data-fff-tilt]').forEach(function (el) {
      if (el.__fffT) return; el.__fffT = true;
      var max = parseFloat(el.getAttribute('data-fff-tilt')) || cfg.tilt.max;
      var o = { el: el, rx: 0, ry: 0, trx: 0, try_: 0 }; el.classList.add('fff-tilt');
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect(); var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        o.try_ = x * max * 2; o.trx = -y * max * 2;
        el.style.setProperty('--fff-lx', (x * 100 + 50).toFixed(1) + '%'); el.style.setProperty('--fff-ly', (y * 100 + 50).toFixed(1) + '%');
      }, { passive: true });
      el.addEventListener('pointerleave', function () { o.trx = 0; o.try_ = 0; });
      tilts.push(o);
    });
  }
  function initMagnetic(root) {
    if (tier !== 'full') return;
    root.querySelectorAll('[data-fff-magnetic]').forEach(function (el) {
      if (el.__fffM) return; el.__fffM = true;
      var o = { el: el, x: 0, y: 0, tx: 0, ty: 0 }; el.classList.add('fff-magnetic');
      var move = function (e) {
        var r = el.getBoundingClientRect(), cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        var dx = e.clientX - cx, dy = e.clientY - cy, d = Math.hypot(dx, dy), R = cfg.magnetic.radius;
        if (d < R) { var f = (1 - d / R) * cfg.magnetic.strength; o.tx = dx * f; o.ty = dy * f; } else { o.tx = 0; o.ty = 0; }
      };
      window.addEventListener('pointermove', move, { passive: true });
      mags.push(o);
    });
  }

  /* ---------- float (studio suspension) ---------- */
  function initFloat(root) {
    if (tier === 'reduced' || !window.gsap) return;
    root.querySelectorAll('[data-fff-float]').forEach(function (el, i) {
      if (el.__fffF) return; el.__fffF = true; el.classList.add('fff-float');
      var y = cfg.float.y * K, r = cfg.float.rot * K, p = cfg.float.period + (i % 3) * 0.7;
      gsap.to(el, { '--fff-fy': -y + 'px', '--fff-fr': r + 'deg', duration: p, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: -Math.random() * p });
      var sh = el.querySelector('.fff-shadow');
      if (sh) gsap.to(sh, { scaleX: 0.86, opacity: 0.55, duration: p, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: -Math.random() * p });
    });
  }

  /* ---------- reveals ---------- */
  function splitLines(el) {
    if (el.__fffSplit) return; el.__fffSplit = true;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(function (t) {
            if (t.trim()) { var w = document.createElement('span'); w.className = 'fff-w'; var i = document.createElement('i'); i.textContent = t; w.appendChild(i); frag.appendChild(w); }
            else frag.appendChild(document.createTextNode(t));
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !/^(BR|IMG|SVG)$/.test(n.tagName)) walk(n);
      });
    })(el);
  }
  function initReveals(root) {
    var els = root.querySelectorAll('[data-fff-reveal]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target; io.unobserve(el);
        var d = parseInt(el.getAttribute('data-fff-delay'), 10) || 0;
        setTimeout(function () { el.classList.add('is-in'); }, d);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    els.forEach(function (el) {
      if (el.__fffR) return; el.__fffR = true;
      var type = el.getAttribute('data-fff-reveal') || 'mask-up';
      if (tier === 'reduced') type = 'fade';
      if (type === 'lines') { splitLines(el); el.querySelectorAll('.fff-w').forEach(function (w, i) { w.style.transitionDelay = (i * cfg.reveal.stagger * 0.55).toFixed(2) + 's'; }); }
      el.setAttribute('data-fff-reveal', type); el.classList.add('fff-reveal');
      io.observe(el);
    });
  }

  /* ---------- scroll scenes: depth planes + scrubbed timelines ---------- */
  var triggers = [];
  function initScenes(root) {
    if (tier === 'reduced' || !window.gsap || !window.ScrollTrigger) return;
    root.querySelectorAll('[data-fff-scene]').forEach(function (sc) {
      if (sc.__fffS) return; sc.__fffS = true;
      var planes = sc.querySelectorAll('[data-fff-depth]');
      planes.forEach(function (p) {
        var d = parseFloat(p.getAttribute('data-fff-depth')) || 0.3;
        p.classList.add('fff-plane');
        gsap.fromTo(p, { '--fff-sy': (d * cfg.depth.strength * K * 0.6) + 'px' }, {
          '--fff-sy': (-d * cfg.depth.strength * K) + 'px', ease: 'none',
          scrollTrigger: { trigger: sc, start: 'top bottom', end: 'bottom top', scrub: cfg.scrub }
        });
      });
      var tl = sc.getAttribute('data-fff-timeline');
      if (tl && tier === 'lite' && sc.hasAttribute('data-fff-lite-static')) tl = null;
      if (tl) {
        try { tl = JSON.parse(tl); } catch (err) { return; }
        var pin = sc.hasAttribute('data-fff-pin') && tier === 'full';
        var len = parseFloat(sc.getAttribute('data-fff-length')) || 220;
        var stage = sc.querySelector('[data-fff-stage]') || sc;
        var t = gsap.timeline({ scrollTrigger: { trigger: sc, start: sc.getAttribute('data-fff-start') || (pin ? 'top top' : 'top 80%'), end: sc.getAttribute('data-fff-end') || (pin ? '+=' + len + '%' : 'bottom 20%'), scrub: cfg.scrub, pin: pin ? stage : false, anticipatePin: 1, invalidateOnRefresh: true } });
        tl.forEach(function (step) {
          var el = step.sel ? sc.querySelectorAll(step.sel) : sc; if (!el.length && !step.sel) el = [sc];
          if (!el.length) return;
          var to = Object.assign({ ease: step.ease || 'power2.inOut', duration: step.d || 1 }, scaleVars(step.to || {}));
          if (step.from) t.fromTo(el, scaleVars(step.from), to, step.at || 0); else t.to(el, to, step.at || 0);
        });
        triggers.push(t);
      }
    });
  }
  function scaleVars(v) { var o = {}; for (var k in v) { var val = v[k]; if (K !== 1 && typeof val === 'number' && /^(x|y|z)$/.test(k)) val = val * K; o[k] = val; } return o; }

  /* ---------- ambient colour per section ---------- */
  function initAmbientSections(root) {
    if (!amb || !window.ScrollTrigger) return;
    root.querySelectorAll('[data-fff-ambient]').forEach(function (el) {
      if (el.__fffA) return; el.__fffA = true;
      var name = el.getAttribute('data-fff-ambient');
      ScrollTrigger.create({ trigger: el, start: 'top 55%', end: 'bottom 45%', onEnter: function () { amb.set(name); }, onEnterBack: function () { amb.set(name); } });
    });
  }

  /* ---------- boot ---------- */
  function initAll(root) {
    root = root || document;
    initReveals(root); initPointerPlanes(root); initTilt(root); initMagnetic(root); initFloat(root);
    root.querySelectorAll('[data-fff-particles]').forEach(function (el) { if (!el.__fffPt) { el.__fffPt = true; initParticles(el); } });
    initScenes(root); initAmbientSections(root);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  }
  function boot() {
    if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    initAmbient();
    // auto-magnetic on primary buttons
    document.querySelectorAll('.fffh__btn, .button--primary, .product-form__submit, .fffb__cta, .fff-story .button, .ffg__btn, .fffc__cta, .quick-add__submit').forEach(function (b) { b.setAttribute('data-fff-magnetic', ''); });
    document.querySelectorAll('.card-wrapper .card, .product__media-wrapper .product__media').forEach(function (c) { c.setAttribute('data-fff-tilt', '3'); c.style.position = c.style.position || 'relative'; });
    initAll(document);
    if (tier === 'full') raf(loop);
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
    window.addEventListener('load', function () { if (window.ScrollTrigger) setTimeout(ScrollTrigger.refresh, 300); });
  }
  window.__fffMotion = { tier: tier, init: initAll, cfg: cfg };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
